// Swipe triage on phones (U10), as pure rules: left archives, right snoozes, both one-press
// reversible changes with Undo. Sends are never swipeable. A swipe must first be clearly sideways
// (the axis locks after a few pixels; mostly-vertical movement is a scroll and is let go), and it
// only counts past a clear distance, so reading and scrolling never trigger it.
import { isReversibleActionKind } from "@shared/actionKinds";
import type { CardAction } from "@shared/core";
import { isInjected } from "@web/feed/actions";

export type SwipeDirection = "archive" | "snooze";
export type SwipeAxis = "pending" | "x" | "y";

/** Movement before deciding which way the finger is going. */
export const SWIPE_SLOP_PX = 10;
/** Sideways must beat vertical by this much to count as a swipe. */
export const SWIPE_AXIS_RATIO = 1.5;
/** How far to pull before letting go archives or snoozes. */
export const SWIPE_COMMIT_PX = 96;
/** On a narrow row, a share of its width instead. */
export const SWIPE_COMMIT_FRACTION = 0.4;

export function lockAxis(dx: number, dy: number): SwipeAxis {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (Math.max(ax, ay) < SWIPE_SLOP_PX) return "pending";
  return ax > ay * SWIPE_AXIS_RATIO ? "x" : "y";
}

export function commitDistance(width: number): number {
  return width > 0 ? Math.min(SWIPE_COMMIT_PX, width * SWIPE_COMMIT_FRACTION) : SWIPE_COMMIT_PX;
}

/** What letting go at `dx` does: left archives, right snoozes, short of the line nothing. */
export function swipeOutcome(
  dx: number,
  width: number,
  allowed: { archive: boolean; snooze: boolean },
): SwipeDirection | null {
  const line = commitDistance(width);
  if (dx <= -line && allowed.archive) return "archive";
  if (dx >= line && allowed.snooze) return "snooze";
  return null;
}

/** How far the row follows the finger: freely to the line, then with resistance. */
export function followOffset(
  dx: number,
  width: number,
  allowed: { archive: boolean; snooze: boolean },
) {
  if ((dx < 0 && !allowed.archive) || (dx > 0 && !allowed.snooze)) return dx / 6;
  const line = commitDistance(width);
  const over = Math.abs(dx) - line;
  if (over <= 0) return dx;
  return Math.sign(dx) * (line + over / 3);
}

/** One finger on a row, from touch down to lift. */
export interface SwipeTrack {
  startX: number;
  startY: number;
  axis: SwipeAxis;
  dx: number;
}

export function trackStart(x: number, y: number): SwipeTrack {
  return { startX: x, startY: y, axis: "pending", dx: 0 };
}

/** Once the movement reads as a scroll it stays a scroll until the finger lifts. */
export function trackMove(track: SwipeTrack, x: number, y: number): SwipeTrack {
  const dx = x - track.startX;
  const axis = track.axis === "pending" ? lockAxis(dx, y - track.startY) : track.axis;
  return { ...track, axis, dx: axis === "x" ? dx : 0 };
}

export function trackEnd(
  track: SwipeTrack,
  width: number,
  allowed: { archive: boolean; snooze: boolean },
): SwipeDirection | null {
  return track.axis === "x" ? swipeOutcome(track.dx, width, allowed) : null;
}

/** Elements a swipe never starts on: anything that takes a tap, text or a selection. */
export const NO_SWIPE_SELECTOR =
  'input, textarea, select, button, a, [contenteditable="true"], [role="switch"], [data-no-swipe]';

/**
 * The card's archive step, if a swipe may run it: the injected cleanup when it archives, which is
 * a reversible mailbox change (one press with Undo). A feed whose cleanup does something else, and
 * anything that needs arm → confirm, is not swiped.
 */
export function swipeArchiveAction(actions: readonly CardAction[]): CardAction | null {
  const cleanup = actions.find((a) => a.behavior === "default_cleanup");
  return cleanup?.actionKind === "gmail.archive_thread" &&
    isReversibleActionKind(cleanup.actionKind)
    ? cleanup
    : null;
}

/** The highlighted next step: the recommended action, else the primary one, else the first. */
export function highlightedAction(actions: readonly CardAction[]): CardAction | null {
  const configured = actions.filter((a) => !isInjected(a));
  const recommended = configured.find(
    (a) => (a as CardAction & { recommended?: boolean }).recommended,
  );
  return recommended ?? configured.find((a) => a.variant === "primary") ?? configured[0] ?? null;
}
